import Logger from "../lib/logger";

class HomeFloatingIconModal {
	NAME = 'HomeFloatingIconModal';

	init() {
		Logger.info(`${this.NAME} > init`);

		const sections = document.querySelectorAll("[data-home-floating-icon-root]");
		if (!sections.length) return;

		sections.forEach((section) => this.initSection(section));
	}

	initSection(section) {
		const trigger = section.querySelector("[data-home-floating-icon-trigger]");
		const triggerAnimationVideo = trigger?.querySelector("[data-home-floating-icon-animation-video]");
		let triggerAnimationGif = trigger?.querySelector("[data-home-floating-icon-animation-gif]");
		const modal = section.querySelector("[data-home-floating-icon-modal]");
		const closeButtons = modal ? modal.querySelectorAll("[data-home-floating-icon-close]") : [];
		const videos = modal ? Array.from(modal.querySelectorAll("[data-home-floating-icon-video]")) : [];
		const reducedMotionQuery = window.matchMedia("(prefers-reduced-motion: reduce)");

		if (!trigger || !modal) return;

		const state = {
			isActive: false,
			isTriggerAnimating: false,
			lastFocused: null,
			playTimeoutId: null,
			triggerAnimationTimeoutId: null,
		};

		const getActiveVideo = () => {
			if (!videos.length) return null;
			const mediaQuery = window.matchMedia("(min-width: 960px)");
			const preferred = videos.find((video) =>
				mediaQuery.matches
					? video.classList.contains("page-home-hero__image-modal-video--desktop")
					: video.classList.contains("page-home-hero__image-modal-video--mobile")
			);
			return preferred || videos[0];
		};

		const stopVideos = () => {
			videos.forEach((video) => {
				video.pause();
				try {
					video.currentTime = 0;
				} catch (error) {
					video.load();
				}
			});
		};

		const clearTriggerAnimationTimeout = () => {
			if (!state.triggerAnimationTimeoutId) return;
			window.clearTimeout(state.triggerAnimationTimeoutId);
			state.triggerAnimationTimeoutId = null;
		};

		const refreshTriggerAnimationGif = () => {
			if (!triggerAnimationGif) return null;
			const nextTriggerAnimationGif = triggerAnimationGif.cloneNode(true);
			triggerAnimationGif.replaceWith(nextTriggerAnimationGif);
			triggerAnimationGif = nextTriggerAnimationGif;
			return triggerAnimationGif;
		};

		const resetTriggerAnimation = () => {
			if (!triggerAnimationVideo && !triggerAnimationGif) return;
			clearTriggerAnimationTimeout();
			state.isTriggerAnimating = false;
			trigger.classList.remove("page-home-hero__floating-icon--animating");

			if (triggerAnimationVideo) {
				triggerAnimationVideo.pause();
				try {
					triggerAnimationVideo.currentTime = 0;
				} catch (error) {
					triggerAnimationVideo.load();
				}
			}

			refreshTriggerAnimationGif();
		};

		const openModal = () => {
			if (state.isActive) return;

			state.isActive = true;
			state.lastFocused = document.activeElement;
			modal.classList.add("page-home-hero__image-modal--active");
			modal.setAttribute("aria-hidden", "false");
			document.documentElement.classList.add("page-home-hero--image-modal-open");
			document.body.classList.add("page-home-hero--image-modal-open");

			const activeVideo = getActiveVideo();
			if (activeVideo) {
				stopVideos();
				if (state.playTimeoutId) {
					window.clearTimeout(state.playTimeoutId);
				}
				state.playTimeoutId = window.setTimeout(() => {
					const playPromise = activeVideo.play();
					if (playPromise && typeof playPromise.catch === "function") {
						playPromise.catch(() => {});
					}
				}, 500);
			}

			if (typeof modal.focus === "function") {
				try {
					modal.focus({ preventScroll: true });
				} catch (error) {
					modal.focus();
				}
			}
		};

		const finishTriggerAnimation = () => {
			if (!state.isTriggerAnimating) return;
			clearTriggerAnimationTimeout();
			state.isTriggerAnimating = false;
			openModal();
		};

		const playTriggerAnimation = () => {
			if ((!triggerAnimationVideo && !triggerAnimationGif) || reducedMotionQuery.matches) {
				openModal();
				return;
			}

			resetTriggerAnimation();
			state.isTriggerAnimating = true;
			trigger.classList.add("page-home-hero__floating-icon--animating");

			const gifDurationMs = triggerAnimationGif
				? Number.parseInt(triggerAnimationGif.dataset.homeFloatingIconAnimationGifDuration, 10)
				: NaN;
			const durationMs = triggerAnimationVideo
				? (
					Number.isFinite(triggerAnimationVideo.duration) && triggerAnimationVideo.duration > 0
						? Math.ceil(triggerAnimationVideo.duration * 1000) + 100
						: 2000
				)
				: (Number.isFinite(gifDurationMs) && gifDurationMs > 0 ? gifDurationMs : 1500);

			state.triggerAnimationTimeoutId = window.setTimeout(finishTriggerAnimation, durationMs);

			if (triggerAnimationVideo) {
				const playPromise = triggerAnimationVideo.play();
				if (playPromise && typeof playPromise.catch === "function") {
					playPromise.catch(() => finishTriggerAnimation());
				}
			}
		};

		const closeModal = () => {
			if (!state.isActive) return;

			state.isActive = false;
			modal.classList.remove("page-home-hero__image-modal--active");
			modal.setAttribute("aria-hidden", "true");
			document.documentElement.classList.remove("page-home-hero--image-modal-open");
			document.body.classList.remove("page-home-hero--image-modal-open");
			if (state.playTimeoutId) {
				window.clearTimeout(state.playTimeoutId);
				state.playTimeoutId = null;
			}
			stopVideos();
			resetTriggerAnimation();

			if (state.lastFocused && typeof state.lastFocused.focus === "function") {
				try {
					state.lastFocused.focus({ preventScroll: true });
				} catch (error) {
					state.lastFocused.focus();
				}
			}
		};

		const onKeydown = (event) => {
			if (!state.isActive) return;
			if (event.key !== "Escape") return;
			event.preventDefault();
			closeModal();
		};

		trigger.addEventListener("click", (event) => {
			event.preventDefault();
			if (state.isActive || state.isTriggerAnimating) return;
			playTriggerAnimation();
		});

		if (triggerAnimationVideo) {
			triggerAnimationVideo.addEventListener("ended", finishTriggerAnimation);
			triggerAnimationVideo.addEventListener("error", finishTriggerAnimation);
		}

		closeButtons.forEach((button) => button.addEventListener("click", closeModal));

		modal.addEventListener("click", (event) => {
			if (event.target !== modal) return;
			closeModal();
		});

		document.addEventListener("keydown", onKeydown);
	}
}

export default HomeFloatingIconModal;
